import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnInit,
	QueryList,
	ViewChild,
	ViewChildren,
} from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatStepper } from "@angular/material/stepper";
import { MatTabGroup } from "@angular/material/tabs";
import { ActivatedRoute } from "@angular/router";
import moment from "moment";
import { forkJoin, of, Subscription } from "rxjs";
import { catchError, map } from "rxjs/operators";

import {
	buildTs1ReportPreviewContext,
	isTs1V3ReportSummary,
	Ts1ReportPreviewContext,
} from "src/app/cbta-web/report/report-summary/ts1-report-preview.utils";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { Observation, ReportSummary, ReportSummaryComment } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { buildSummaryReportTitle } from "src/app/shared/utils/form-type-label";
import { getRankLabel } from "src/app/shared/utils/rank-label.util";
import { HelperService } from "src/app/util/helper.service";
import { SESSION } from "src/session.config";
import {
	formatRecommendationClearance,
	formatRecommendationObjective,
	formatRecommendationYesNo,
	isRecommendationFormType,
	isRecommendationTraining,
	recommendationSessionLabel,
} from "../../shared-v2/utils/recommendation-summary.utils";

@Component({
	selector: "app-quick-rating-summary-details",
	templateUrl: "./quick-rating-summary-details.component.html",
	styleUrls: ["./quick-rating-summary-details.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class QuickRatingSummaryDetailsComponent implements OnInit {
	@ViewChildren("summaryReport") summaryReport!: QueryList<ElementRef>;
	@ViewChildren("maneuverBreakdownRef") maneuverBreakdownRef!: QueryList<ElementRef>;
	@ViewChildren("instructorNotes") instructorNotes!: QueryList<ElementRef>;
	@ViewChild("maneuverTabs") maneuverTabs!: MatTabGroup;

	createNewForm!: UntypedFormGroup;
	get maneuvers(): UntypedFormArray {
		return this.createNewForm.get("maneuvers") as UntypedFormArray;
	}

	candidateList: ReportSummary[] = [];
	candidateIds: string[] = [];

	current = 0;
	prev = -1;

	comments: ReportSummaryComment[] = [];
	otherComments: string[] = [];
	sessionList: string[] = [];

	isTS1 = false;
	isTS1V3 = false;
	isTS6 = false;
	isTS3 = false;
	isTS5 = false;
	isCC = false;

	ts1PreviewContexts: Array<Ts1ReportPreviewContext | null> = [];
	ts1V3PageIndex = 0;

	candidateSignature?: string;
	instructorSignature?: string;

	routeSub!: Subscription;
	summarySub!: Subscription;

	lastObservations: Observation[] = []; // cached for TS-1 "Add New"
	fixedManeuvers: any[] = []; // cached for non TS-1 header

	private _reportSummary?: ReportSummary;
	pdfAttachement: any[] = [];
	selectedOBList: any = {};
	observationForm!: UntypedFormGroup;
	meneuverNameList: any[] = [];
	obAddRemoveList: any[] = [];

	private _ratingDatasets: any[] = [];
	private _ratingLabels: any[] = [];
	private _manueverChartData: any[] = [];

	constructor(
		private httpService: HttpService,
		private sharedService: SharedService,
		private route: ActivatedRoute,
		public helperService: HelperService,
		private loaderService: LoaderService,
		private fb: UntypedFormBuilder,
		private cdr: ChangeDetectorRef,
		private roleService: RoleService,
	) {}

	// ========= Lifecycle =========
	ngOnInit(): void {
		this.initCreateNewForm();
		this.sharedService.setActivePage(RouteType.RATINGS);
		this.loaderService.setLoading(true);

		this.candidateIds = this.loadCandidateIdsFromStorage();
		if (!this.candidateIds.length) {
			this.loaderService.setLoading(false);
			return;
		}
		this.fetchAllSummaries(this.candidateIds);
	}

	ngOnDestroy(): void {
		if (this.routeSub) this.routeSub.unsubscribe();
		if (this.summarySub) this.summarySub.unsubscribe();
	}

	// ========= Storage & Fetch =========
	private loadCandidateIdsFromStorage(): string[] {
		const raw = localStorage.getItem(SESSION.SESSION_KEY_CANDIDATES) ?? "[]";
		try {
			const parsed = JSON.parse(raw);
			if (Array.isArray(parsed)) {
				// up to 4
				return parsed.slice(0, 4).filter((x) => typeof x === "string" && x.trim().length);
			}
			return [];
		} catch {
			return [];
		}
	}

	private fetchAllSummaries(ids: string[]) {
		const role = this.roleService.getUserRole();
		const requests = ids.map((id) =>
			this.httpService.getSummary(id, { role }).pipe(
				map((resp: any) => resp?.data as ReportSummary),
				catchError(() => of(undefined as unknown as ReportSummary)),
			),
		);

		this.summarySub = forkJoin(requests).subscribe(
			(results) => {
				console.log("this summary", results);

				this.candidateList = (results.filter(Boolean) as ReportSummary[]) ?? [];
				this.ts1PreviewContexts = this.candidateList.map((summary, index) =>
					isTs1V3ReportSummary(summary) ? buildTs1ReportPreviewContext(summary, this.fb, ids[index]) : null,
				);
				if (this.candidateList.length) {
					this.current = Math.min(this.current, this.candidateList.length - 1);
					this.setReportSummary(this.candidateList[this.current]);
					this.downloadSummary();
					this.initObservationsForm();
				}
				this.cdr.detectChanges();
				this.loaderService.setLoading(false);
			},
			() => this.loaderService.setLoading(false),
		);
	}

	// ========= Report summary =========
	setReportSummary(reportSummary: ReportSummary) {
		this.comments = reportSummary?.trainerExaminerNotes?.summary;

		this.otherComments = reportSummary?.trainerExaminerNotes?.otherTrainingAndObservations;
		this.sessionList = reportSummary?.sessionDetails?.sessionDetails ?? [];
		this.candidateSignature = this.resolveSignatureImage(reportSummary?.sessionDetails?.traineeSignature);
		this.instructorSignature = this.resolveSignatureImage(reportSummary?.sessionDetails?.instructorSignature);
		this.updateFormTypeFlags(reportSummary);
		this.isTS1V3 = isTs1V3ReportSummary(reportSummary);
		this.ts1V3PageIndex = 0;
		this._reportSummary = reportSummary;
	}

	private updateFormTypeFlags(reportSummary?: ReportSummary): void {
		const formType = reportSummary?.formType;
		this.isTS6 = formType === CONSTANT.FORM_TYPE.ts_6;
		this.isTS1 = formType === CONSTANT.FORM_TYPE.ts_1;
		this.isTS3 = formType === CONSTANT.FORM_TYPE.ts_3;
		this.isTS5 = formType === CONSTANT.FORM_TYPE.ts_5;
		this.isCC =
			formType === CONSTANT.FORM_TYPE.cabin_crew || formType === CONSTANT.FORM_TYPE.cabin_crew_instructor;
	}

	isNoGradingSignOffForm(candidate?: ReportSummary): boolean {
		const formType = candidate?.formType;
		return formType === CONSTANT.FORM_TYPE.ts_5 || formType === CONSTANT.FORM_TYPE.ts_6;
	}

	isCandidateCC(candidate?: ReportSummary): boolean {
		const formType = candidate?.formType;
		return formType === CONSTANT.FORM_TYPE.cabin_crew || formType === CONSTANT.FORM_TYPE.cabin_crew_instructor;
	}

	showCandidateSignature(candidate?: ReportSummary): boolean {
		const formType = candidate?.formType;
		return (
			formType === CONSTANT.FORM_TYPE.ts_1 ||
			formType === CONSTANT.FORM_TYPE.ts_3 ||
			this.isCandidateCC(candidate)
		);
	}

	hasRecommendationSummary(candidate?: ReportSummary): boolean {
		return (
			isRecommendationFormType(candidate?.formType) &&
			!!candidate?.recommendation &&
			!!(candidate.recommendation.sessionType || candidate.recommendation.sessionTypeDescription)
		);
	}

	resolveSignatureImage(signature: unknown): string {
		if (!signature) return "";

		if (typeof signature === "string") {
			if (signature.startsWith("data:")) return signature;
			return `data:image/png;base64,${signature}`;
		}

		if (ArrayBuffer.isView(signature)) {
			return this.helperService.bufferDataToImageUrl(Array.from(new Uint8Array(signature.buffer)));
		}

		const bufferData = (signature as { data?: number[] })?.data;
		return this.helperService.bufferDataToImageUrl(bufferData);
	}

	get reportSummary(): ReportSummary | undefined {
		return this._reportSummary;
	}

	get summaryReportTitle(): string {
		return buildSummaryReportTitle(this._reportSummary?.formType);
	}

	// ========= UI helpers =========
	join(arr?: string[] | { description?: string }[]) {
		if (!arr?.length) return "";
		if (typeof arr[0] === "string") {
			return (arr as string[]).join(", ");
		}
		return (arr as { description?: string }[])
			.map((x) => x?.description ?? "")
			.filter(Boolean)
			.join(", ");
	}

	getRank(rank?: string) {
		return getRankLabel(rank);
	}

	goBack(stepper: MatStepper) {
		this.gotoTop(stepper, "back");
	}

	goNext(stepper: MatStepper) {
		this.gotoTop(stepper, "next");
	}

	hasTs1V3ManeuverBreakdown(summary?: ReportSummary): boolean {
		return (summary?.ts1V3PreviewSummary?.maneuverBreakdownView?.length ?? 0) > 0;
	}

	goBackTs1V3() {
		if (this.ts1V3PageIndex > 0) {
			this.ts1V3PageIndex = 0;
			this.scrollQuickSummaryToTop();
			return;
		}
	}

	goNextTs1V3() {
		const candidate = this.candidateList[this.current];
		if (this.ts1V3PageIndex === 0 && this.hasTs1V3ManeuverBreakdown(candidate)) {
			this.ts1V3PageIndex = 1;
			this.scrollQuickSummaryToTop();
		}
	}

	private scrollQuickSummaryToTop(): void {
		setTimeout(() => {
			const element = document.getElementById("cbta-web-page-container") as HTMLElement;
			if (element) element.scrollTop = 0;
		}, 100);
	}

	gotoTop(stepper: MatStepper, direction: "next" | "back") {
		setTimeout(() => {
			const element = document.getElementById("cbta-web-page-container") as HTMLElement;
			if (element) element.scrollTop = 0;
			if (direction === "next") {
				stepper.next();
			} else if (stepper.selectedIndex > 0) {
				stepper.previous();
			}
		}, 100);
	}

	// ========= Candidate navigation (multi-candidate safe) =========
	onPrev() {
		if (this.current > 0) {
			this.prev = this.current--;
			if (this.candidateList[this.current]) {
				this.setReportSummary(this.candidateList[this.current]);
				this.initObservationsForm();
			}
		}
	}

	onNext() {
		if (this.current < this.candidateList.length - 1) {
			this.prev = this.current++;
			if (this.candidateList[this.current]) {
				this.setReportSummary(this.candidateList[this.current]);
				this.initObservationsForm();
			}
		}
	}

	isLeftTransition(idx: number): boolean {
		return this.current === idx ? this.prev > this.current : this.prev < this.current;
	}

	trackCandidate = (i: number, c: ReportSummary) =>
		c?.candidateDetails?.staffNumber ?? (c as any)?.candidateDetails?.id ?? i;

	formatSummaryDate(dateString?: string): string {
		return dateString ? moment(dateString).format(CONSTANT.DATE_FORMAT.DISPLAY_DATE_FORMAT) : "-";
	}

	readonly isRecommendationFormType = isRecommendationFormType;
	readonly isRecommendationTraining = isRecommendationTraining;
	readonly recommendationSessionLabel = recommendationSessionLabel;
	readonly formatRecommendationYesNo = formatRecommendationYesNo;
	readonly formatRecommendationObjective = formatRecommendationObjective;
	readonly formatRecommendationClearance = formatRecommendationClearance;
	readonly isTs1V3ReportSummary = isTs1V3ReportSummary;

	getPdfName(candidate: ReportSummary) {
		const sessionDetails = candidate?.sessionDetails?.sessionDetails?.join("_");
		const trainingDate = moment(candidate?.trainingDate).format("DDMMYY");
		const name = candidate?.candidateDetails?.name;
		return `${sessionDetails}_${name}_${trainingDate}.pdf`.toUpperCase();
	}

	downloadSummary() {
		const role = this.roleService.getUserRole();
		for (const id of this.candidateIds) {
			this.httpService.generateSummary(id, { downloadOnly: false, role }).subscribe({
				next: (result) => {
					if (this.roleService.isAdmin) {
						const filename = result?.data?.report_file_signed_url;
						if (filename) this.sharedService.download(filename);
					}
				},
				error: (e) => console.error(e),
			});
		}
	}

	// ========= Form building =========
	private initCreateNewForm(): void {
		this.createNewForm = this.fb.group({
			formType: ["", [Validators.required]],
			formName: ["", [Validators.required]],
			syllabusStartDate: [new Date(), [Validators.required]],
			syllabusEndDate: [new Date(), [Validators.required]],
			sessions: this.fb.group({}),
			maneuvers: this.fb.array([]),
		});

		this.maneuvers.clearValidators();
		this.maneuvers.updateValueAndValidity({ emitEvent: false, onlySelf: true });
	}

	initObservationsForm(): void {
		this.observationForm = this.fb.group({
			observations: this.fb.array([]),
		});

		setTimeout(() => {
			this.reformatAllOBs();
		}, 100);
	}

	private makeObsValue() {
		return {
			totalCount: 0,
			currentCount: 0,
			preDefined: true,
			lastAction: CONSTANT.OB_LAST_ACTION.CREATE,
			isDisabled: false,
			showButton: false,
		};
	}

	/** CC/CCI: allow multi-segment categories like CC_COM_1 (same TS-1-only validator rule) */
	private buildManeuverCrewFromObservations(observations: Observation[], formType: string): UntypedFormGroup {
		const byCategory = new Map<string, Array<{ key: string; num: number }>>();
		const seen = new Set<string>();

		for (const o of observations || []) {
			const code = (o.code || "").trim();
			if (!code || seen.has(code)) continue;
			seen.add(code);

			const m = /^([a-zA-Z]+(?:_[a-zA-Z]+)*)_(\d+)$/.exec(code);
			if (!m) continue;
			const cat = m[1].toUpperCase();
			const num = Number(m[2]);
			const key = `${cat}${num}`;
			if (!byCategory.has(cat)) byCategory.set(cat, []);
			byCategory.get(cat)!.push({ key, num });
		}

		const categoryGroups: Record<string, any> = {};
		for (const [cat, items] of byCategory.entries()) {
			items.sort((a, b) => a.num - b.num);
			const controls: Record<string, any> = {};
			for (const it of items) controls[it.key] = [{ value: this.makeObsValue(), disabled: false }];
			categoryGroups[cat] = this.fb.group(controls);
		}

		const group = this.fb.group({
			maneuver: ["", [Validators.required]],
			formType: [formType],
			maneuverCode: [""],
			assessmentId: [""],
			traineeAssessmentId: [""],
			candidateId: [""],
			selectedOBs: [[]],
			safetyOutcome: [-2],
			repeat: [0],
			...categoryGroups,
		});

		return group;
	}

	private buildManeuverFromObservations(observations: Observation[], formType: string): UntypedFormGroup {
		const byCategory = new Map<string, Array<{ key: string; num: number }>>();
		const seen = new Set<string>();

		for (const o of observations || []) {
			const code = (o.code || "").trim();
			if (!code || seen.has(code)) continue;
			seen.add(code);

			const m = /^([a-zA-Z]+)_(\d+)$/.exec(code);
			if (!m) continue;
			const cat = m[1].toUpperCase();
			const num = Number(m[2]);
			const key = `${cat}${num}`;
			if (!byCategory.has(cat)) byCategory.set(cat, []);
			byCategory.get(cat)!.push({ key, num });
		}

		const categoryGroups: Record<string, any> = {};
		for (const [cat, items] of byCategory.entries()) {
			items.sort((a, b) => a.num - b.num);
			const controls: Record<string, any> = {};
			for (const it of items) controls[it.key] = [{ value: this.makeObsValue(), disabled: false }];
			categoryGroups[cat] = this.fb.group(controls);
		}

		const group = this.fb.group({
			maneuver: ["", []],
			formType: [formType],
			maneuverCode: [""],
			assessmentId: [""],
			traineeAssessmentId: [""],
			candidateId: [""],
			selectedOBs: [[]],
			safetyOutcome: [-2],
			repeat: [0],
			...categoryGroups,
		});

		return group;
	}

	/** For non TS-1: set maneuver header code/desc */
	private setManeuverOnGroup(mfg: UntypedFormGroup, opt: { code: string; description?: string }) {
		mfg.get("maneuver")?.setValue({ code: opt.code, description: opt.description ?? "" });
		mfg.get("maneuver")?.markAsDirty();
		mfg.get("maneuver")?.updateValueAndValidity({ emitEvent: false });
	}

	transformObservation(fm: any) {
		const fmObs = fm?.observations ?? [];
		if (!fmObs.length) return [];
		const fmByCode = new Map<string, any>();
		for (const it of fmObs) if (it?.code) fmByCode.set(it.code, it);

		const all = this.sharedService.observations ?? [];
		const out: any[] = [];
		for (const o of all) {
			const match = fmByCode.get(o?.code);
			if (match) out.push({ ...o, fixedManeuver: match });
		}
		return out;
	}

	reformatAllOBs(): void {
		const reportSummary = this.candidateList[this.current] as any;
		if (isTs1V3ReportSummary(reportSummary)) {
			return;
		}

		this.meneuverNameList = [];
		this.obAddRemoveList = [];
		this.fixedManeuvers = [];
		this.maneuvers.clear();
		this.updateFormTypeFlags(reportSummary);

		const masterData = this.sharedService.formMasterData?.[reportSummary?.formType];
		const maneuvers = masterData?.maneuvers;

		if (this.isTS1) {
			const mdManeuvers = (masterData?.maneuvers ?? []) as Array<{ code: string; description?: string }>;
			Object.keys(reportSummary.maneuverBreakdown)?.forEach((code: any) => {
				const observations: Observation[] = masterData?.observations;
				const mfg = this.buildManeuverFromObservations(observations, reportSummary?.formType);
				const meta = mdManeuvers.find((m) => (m.code || "").toLowerCase() === code.toLowerCase());
				const label = meta?.description || this.prettyCode(code);

				this.meneuverNameList.push(label);

				mfg.get("maneuver")?.patchValue({ code, description: label });
				this.maneuvers.push(mfg);
				this.lastObservations = observations;
			});
		} else if (this.isCC) {
			console.log("reportSummary?.formType", reportSummary?.formType);
			maneuvers.forEach((m: any) => {
				const obsForM = masterData?.observations.filter((o: any) => o.fixed_maneuver === m.code);
				const mfg = this.buildManeuverCrewFromObservations(obsForM, reportSummary?.formType);
				this.maneuvers.push(mfg);
				this.fixedManeuvers.push(m);
				this.setManeuverOnGroup(mfg, { code: m.code, description: m.description });
			});
		} else {
			maneuvers?.forEach((m: any) => {
				const observationForManeuver = masterData?.observations.filter((o: any) => o.fixed_maneuver === m.code);
				console.log("observationForManeuver", observationForManeuver);
				const mfg = this.buildManeuverFromObservations(observationForManeuver, reportSummary?.formType);

				this.maneuvers.push(mfg);
				this.fixedManeuvers.push(m);

				if (this.isTS6) {
					this.setManeuverOnGroup(mfg, { code: reportSummary?.formType, description: "" });
				} else {
					this.setManeuverOnGroup(mfg, { code: m.code, description: m.description });
				}
			});
		}

		console.log("reportSummary?.maneuverBreakdown", reportSummary?.maneuverBreakdown);

		// Patch maneuver breakdown values
		reportSummary?.maneuverBreakdown &&
			Object.keys(reportSummary?.maneuverBreakdown).forEach((observationKey, i) => {
				const observationValue = reportSummary?.maneuverBreakdown[observationKey];
				console.log("observationValue", observationValue);
				const observationControlManeuver: any = this.maneuvers.at(i);

				Object.keys(observationValue ?? {}).forEach((code: string) => {
					let obCtrl = null;
					if (!this.isCC) {
						const [cat, num] = String(code).split("_", 2);
						const groupKey = (cat || "").toUpperCase();
						const obKey = `${groupKey}${num}`;

						const mfg = this.maneuvers.at(i) as UntypedFormGroup;
						const catGroup = mfg.get(groupKey) as UntypedFormGroup | null;

						if (!catGroup) return;

						obCtrl = catGroup.get(obKey) as any;
						if (!obCtrl) return;
					} else {
						const [prefix, cat, num] = String(code).split("_", 3);
						const groupKey = (prefix + "_" + cat || "").toUpperCase();
						const obKey = `${groupKey}${num}`;

						const mfg = this.maneuvers.at(i) as UntypedFormGroup;
						const catGroup = mfg.get(groupKey) as UntypedFormGroup | null;

						if (!catGroup) return;

						obCtrl = catGroup.get(obKey) as any;
						if (!obCtrl) return;
					}

					const payload = (observationValue as any)[code] ?? {};
					const currentCount = payload.activeAttempt ?? payload.attempt ?? 2;
					const totalCount = payload.totalAttempt ?? payload.attempt ?? 2;

					const lastAction =
						payload?.isRemoved === true
							? CONSTANT.OB_LAST_ACTION.REMOVED
							: payload?.isAdded === true
								? CONSTANT.OB_LAST_ACTION.ADDED
								: CONSTANT.OB_LAST_ACTION.NO_ACTION;

					const prev = (obCtrl.getRawValue?.() ?? obCtrl.value) || {};
					const next = {
						...prev,
						currentCount,
						totalCount,
						lastAction: lastAction,
						isRemoved: payload.isRemoved,
						preDefined: payload.isOriginal,
						isDisabled: payload.isDisabled,
					};
					obCtrl.patchValue(next, { emitEvent: false });
				});

				(observationControlManeuver as UntypedFormGroup).updateValueAndValidity({
					emitEvent: false,
				});
				this.createNewForm.updateValueAndValidity({ emitEvent: false });
				this.cdr.detectChanges();
			});

		console.log("this.maneuver apakah", this.maneuvers);
	}

	showExpansion(maneuver: any): boolean {
		if (!maneuver) return false;

		const v = (maneuver as any)?.getRawValue();
		if (!v || typeof v !== "object") return false;

		const baseFields = new Set([
			"maneuver",
			"maneuverCode",
			"formType",
			"assessmentId",
			"traineeAssessmentId",
			"candidateId",
			"selectedOBs",
			"safetyOutcome",
			"repeat",
		]);

		// remove base keys; check remaining count
		const remainingKeys = Object.keys(v).filter((k) => !baseFields.has(k));

		return remainingKeys.length > 1; // "more than one" → true, else false
	}

	private prettyCode(v: string): string {
		return (v || "")
			.replace(/[_-]+/g, " ")
			.replace(/\s+/g, " ")
			.trim()
			.replace(/\b\w/g, (c) => c.toUpperCase());
	}

	// ========= Accessors =========
	get observations() {
		return this.observationForm.controls["observations"] as UntypedFormArray;
	}

	get maneuver() {
		return this.observations.get("maneuver")!;
	}

	get ratingLabels() {
		const labels = this.reportSummary?.chartData?.radarChartData?.labels ?? [];

		return labels;
	}

	get ratingDatasets() {
		const datasets = this.reportSummary?.chartData?.radarChartData?.datasets ?? [];
		return datasets;
	}

	get manueverChartData() {
		const data = this.reportSummary?.chartData?.manueverChartData ?? [];

		return data;
	}
}
